import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
/* Components */
import { Modal, Form, InputGroup } from "react-bootstrap";
import WrongNetworkAlert from "./WrongNetworkAlert";
import TxButton from "../CommonForBoth/TxButton";
/* Helpers */
import { strToDec, strToDecZ, strToBN } from "@/helpers/number_format_helper";
import { isTransactionValid } from "@/helpers/store_helper";
import { useModalTxError } from "@/hooks/useModalTxError";
/* Store */
import { selectEthCallMultiple, selectLastTransact } from "@ensuro/ethereum-store";
import { selectCurrent } from "@store/eTokens/selectors";
import { selectChainId } from "@/store/user/selectors";
/* i18n */
import { connect, useDispatch } from "react-redux";
import { withTranslation } from "react-i18next";
import { chain } from "@/config";
import "./style.scss";

const makeEthCalls = function (userAddress, etkAddress, wrapperAddress) {
  return [
    { address: etkAddress, abi: "EToken", method: "allowance", args: [userAddress, wrapperAddress] },
    { address: etkAddress, abi: "EToken", method: "balanceOf", args: [userAddress] },
  ];
};

const getTxHash = (tx) => tx?.hash || tx?.txHash || tx?.transactionHash;
const getTxMethod = (tx) => tx?.method;
const getTxStatus = (tx) => tx?.state || tx?.status;

const isConfirmedTx = (tx) => {
  if (!tx) return false;
  if (tx.receipt) return true;
  const status = getTxStatus(tx);
  return status === "MINED";
};

const WrapModal = ({
  t,
  user,
  userAddress,
  lastTransaction,
  show,
  onHide,
  allowance,
  balance,
  wrapperAddress,
  etkAddress,
  etkSymbol,
  chainId,
  openedAfterApprove = false,
}) => {
  const dispatch = useDispatch();
  const { errorToShow, clearError, wrapDispatch } = useModalTxError(lastTransaction);
  const [userAmount, setUserAmount] = useState("");
  const ready = allowance.state === "LOADED" && balance.state === "LOADED" && chainId === chain.id;
  const lastRefreshedHash = useRef(null);

  const handleHide = () => {
    clearError();
    onHide();
  };

  useEffect(() => {
    if (userAddress && etkAddress && wrapperAddress) {
      dispatch({
        type: "ETH_ADD_SUBSCRIPTION",
        key: "wrapModal",
        componentEthCalls: makeEthCalls(userAddress, etkAddress, wrapperAddress),
      });
    }
    return () => {
      dispatch({ type: "ETH_REMOVE_SUBSCRIPTION", key: "wrapModal" });
    };
  }, [dispatch, etkAddress, wrapperAddress, userAddress]);

  useEffect(() => {
    if (!userAddress || !etkAddress || !wrapperAddress) return;
    if (!isConfirmedTx(lastTransaction)) return;

    const hash = getTxHash(lastTransaction);
    const method = getTxMethod(lastTransaction);

    if (!hash) return;
    if (lastRefreshedHash.current === hash) return;
    if (method !== "approve" && method !== "deposit") return;

    lastRefreshedHash.current = hash;

    dispatch({ type: "ETH_REMOVE_SUBSCRIPTION", key: "wrapModal" });
    dispatch({
      type: "ETH_ADD_SUBSCRIPTION",
      key: "wrapModal",
      componentEthCalls: makeEthCalls(userAddress, etkAddress, wrapperAddress),
    });

    if (method === "deposit") setUserAmount("");
  }, [dispatch, lastTransaction, userAddress, etkAddress, wrapperAddress]);

  const invalidBalance = () => {
    return !balance.value || (strToDec(userAmount) && strToDec(userAmount).gt(balance.value));
  };

  const requiresApprove = () => {
    const ua = strToDec(userAmount);
    if (ua === undefined) return false;
    if (allowance.value && allowance.value.gte(ua)) return false;
    return true;
  };
  const showCheckingAllowance = openedAfterApprove && requiresApprove();

  const dispatchTransfer = () => {
    if (!isTransactionValid(lastTransaction) || !userAddress) return;
    const amountBN = strToBN(userAmount);
    if (amountBN == null) return;
    if (requiresApprove()) {
      dispatch({
        type: "ETH_TRANSACT",
        address: etkAddress,
        abi: "EToken",
        method: "approve",
        args: [wrapperAddress, amountBN],
      });
    } else {
      dispatch({
        type: "ETH_TRANSACT",
        address: wrapperAddress,
        abi: "WEToken",
        method: "deposit",
        args: [amountBN, userAddress],
      });
    }
  };

  return (
    <Modal show={show} centered onHide={handleHide}>
      <Modal.Header closeButton>
        <Modal.Title>
          {t("Wrap")} {etkSymbol}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {chainId !== chain.id && <WrongNetworkAlert />}
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>{t("Amount")}</Form.Label>
            <InputGroup>
              <Form.Control
                placeholder="0.00"
                id="wrap-amount"
                isInvalid={userAmount !== "" && (!strToDecZ(userAmount) || invalidBalance())}
                value={userAmount}
                onChange={(e) => setUserAmount(e.target.value || "")}
              />
              <InputGroup.Text>{etkSymbol}</InputGroup.Text>
              <Form.Control.Feedback type="invalid">
                {!strToDecZ(userAmount)
                  ? t("Please provide a valid amount.")
                  : invalidBalance() && <span>{t("Your balance is not enough.")}</span>}
              </Form.Control.Feedback>
            </InputGroup>
            <p className="align-end">
              {t("Your balance")}: {balance.value != null ? balance.value * 1 : "—"}
              <strong
                onClick={() => setUserAmount(balance.value != null ? balance.value.toString() : "")}
                className="btn-text"
              >
                {t("MAX")}
              </strong>
            </p>
          </Form.Group>

          {showCheckingAllowance && <p>{t("Checking allowance...")}</p>}
          {requiresApprove() && !showCheckingAllowance && <p>{t("Please approve before wrapping")}</p>}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <em style={{ color: "red" }}>{errorToShow ?? ""}</em>
        <TxButton
          chainId={chainId}
          onClick={wrapDispatch(dispatchTransfer)}
          disabled={
            showCheckingAllowance || !ready || !strToDecZ(userAmount) || invalidBalance() || (user && !user.canTransact)
          }
          text={requiresApprove() ? t("Approve") : t("Wrap")}
        />
      </Modal.Footer>
      {user && !user.canTransact && (
        <p className="read-only">{t("Read-only mode. Connect to a wallet to perform transactions.")}</p>
      )}
    </Modal>
  );
};

WrapModal.propTypes = {
  t: PropTypes.any,
};

const mapStateToProps = (state) => {
  const user = state.UserReducer;
  const userAddress = user?.address;
  const etk = selectCurrent(state.ETokensReducer);
  const etkAddress = etk?.address;
  const etkSymbol = etk?.symbol;
  const wrapperAddress = etk?.custom_data?.wrapper?.address;
  const lastTransaction = selectLastTransact(state.EthereumReducer);
  const chainId = selectChainId(state.UserReducer);

  const callsResult =
    !userAddress || !wrapperAddress
      ? null
      : selectEthCallMultiple(state.EthereumReducer, makeEthCalls(userAddress, etkAddress, wrapperAddress));

  const [allowance, balance] = !userAddress || !callsResult ? [{}, {}] : callsResult;

  return {
    user,
    userAddress,
    lastTransaction,
    allowance,
    balance,
    wrapperAddress,
    etkAddress,
    etkSymbol,
    chainId,
  };
};

export default connect(mapStateToProps)(withTranslation()(WrapModal));
